/**
 * Checklist templates for quick setup during onboarding.
 * Templates are organized by category (Airbnb, Homeowner) and bedroom/bathroom counts.
 */

export interface TemplateItem {
  title: string;
  note?: string;
  is_important?: boolean;
}

export interface TemplateSection {
  name: string;
  items: TemplateItem[];
}

export interface ChecklistTemplate {
  id: string;
  name: string;
  category: 'Airbnb Recommendations' | 'Homeowner Recommendations';
  description: string;
  listTitle: string;
  sections: TemplateSection[];
}

export const CHECKLIST_TEMPLATES: ChecklistTemplate[] = [
  // ============================================
  // AIRBNB TEMPLATES
  // ============================================
  {
    id: 'airbnb-1bed-1bath',
    name: 'Airbnb - 1 Bed 1 Bath',
    category: 'Airbnb Recommendations',
    description: 'Perfect for studio apartments or small units',
    listTitle: 'Airbnb Turnover Checklist',
    sections: [
      {
        name: 'Bedroom',
        items: [
          { title: 'Make bed with fresh linens' },
          { title: 'Dust nightstands and surfaces' },
          { title: 'Empty trash' },
          { title: 'Vacuum or mop floors' },
          { title: 'Check under bed for guest items' },
          { title: 'Wipe light switches and door handles' },
        ],
      },
      {
        name: 'Bathroom',
        items: [
          { title: 'Clean toilet inside and out' },
          { title: 'Clean shower or tub' },
          { title: 'Clean sink and counter' },
          { title: 'Clean mirror' },
          { title: 'Mop floor' },
          { title: 'Restock toilet paper' },
          { title: 'Restock fresh towels' },
          { title: 'Empty trash' },
          { title: 'Restock toiletries' },
        ],
      },
      {
        name: 'Kitchen',
        items: [
          { title: 'Wash and put away dishes' },
          { title: 'Clean countertops' },
          { title: 'Clean stovetop' },
          { title: 'Clean microwave inside and out' },
          { title: 'Wipe refrigerator shelves' },
          { title: 'Wipe cabinet fronts' },
          { title: 'Empty trash' },
          { title: 'Mop floor' },
          { title: 'Restock dish soap and sponge' },
        ],
      },
      {
        name: 'Living Room',
        items: [
          { title: 'Dust all surfaces' },
          { title: 'Vacuum or mop floors' },
          { title: 'Fluff pillows and fold blankets' },
          { title: 'Clean TV screen' },
          { title: 'Wipe remotes with disinfectant' },
          { title: 'Empty trash' },
        ],
      },
      {
        name: 'Entryway',
        items: [
          { title: 'Sweep or mop floor' },
          { title: 'Wipe door handle' },
          { title: 'Ensure welcome mat is clean' },
          { title: 'Check lock functions properly' },
        ],
      },
    ],
  },
  {
    id: 'airbnb-2bed-1bath',
    name: 'Airbnb - 2 Bed 1 Bath',
    category: 'Airbnb Recommendations',
    description: 'Common layout for small homes and apartments',
    listTitle: 'Airbnb Turnover Checklist',
    sections: [
      {
        name: 'Bedroom 1',
        items: [
          { title: 'Make bed with fresh linens' },
          { title: 'Dust nightstands and surfaces' },
          { title: 'Empty trash' },
          { title: 'Vacuum or mop floors' },
          { title: 'Check under bed for guest items' },
          { title: 'Wipe light switches and door handles' },
        ],
      },
      {
        name: 'Bedroom 2',
        items: [
          { title: 'Make bed with fresh linens' },
          { title: 'Dust nightstands and surfaces' },
          { title: 'Empty trash' },
          { title: 'Vacuum or mop floors' },
          { title: 'Check under bed for guest items' },
          { title: 'Wipe light switches and door handles' },
        ],
      },
      {
        name: 'Bathroom',
        items: [
          { title: 'Clean toilet inside and out' },
          { title: 'Clean shower or tub' },
          { title: 'Clean sink and counter' },
          { title: 'Clean mirror' },
          { title: 'Mop floor' },
          { title: 'Restock toilet paper' },
          { title: 'Restock fresh towels' },
          { title: 'Empty trash' },
          { title: 'Restock toiletries' },
        ],
      },
      {
        name: 'Kitchen',
        items: [
          { title: 'Wash and put away dishes' },
          { title: 'Clean countertops' },
          { title: 'Clean stovetop' },
          { title: 'Clean microwave inside and out' },
          { title: 'Wipe refrigerator shelves' },
          { title: 'Wipe cabinet fronts' },
          { title: 'Empty trash' },
          { title: 'Mop floor' },
          { title: 'Restock dish soap and sponge' },
        ],
      },
      {
        name: 'Living Room',
        items: [
          { title: 'Dust all surfaces' },
          { title: 'Vacuum or mop floors' },
          { title: 'Fluff pillows and fold blankets' },
          { title: 'Clean TV screen' },
          { title: 'Wipe remotes with disinfectant' },
          { title: 'Empty trash' },
        ],
      },
      {
        name: 'Entryway',
        items: [
          { title: 'Sweep or mop floor' },
          { title: 'Wipe door handle' },
          { title: 'Ensure welcome mat is clean' },
          { title: 'Check lock functions properly' },
        ],
      },
    ],
  },
  {
    id: 'airbnb-2bed-2bath',
    name: 'Airbnb - 2 Bed 2 Bath',
    category: 'Airbnb Recommendations',
    description: 'Standard layout for larger apartments',
    listTitle: 'Airbnb Turnover Checklist',
    sections: [
      {
        name: 'Bedroom 1',
        items: [
          { title: 'Make bed with fresh linens' },
          { title: 'Dust nightstands and surfaces' },
          { title: 'Empty trash' },
          { title: 'Vacuum or mop floors' },
          { title: 'Check under bed for guest items' },
          { title: 'Wipe light switches and door handles' },
        ],
      },
      {
        name: 'Bedroom 2',
        items: [
          { title: 'Make bed with fresh linens' },
          { title: 'Dust nightstands and surfaces' },
          { title: 'Empty trash' },
          { title: 'Vacuum or mop floors' },
          { title: 'Check under bed for guest items' },
          { title: 'Wipe light switches and door handles' },
        ],
      },
      {
        name: 'Bathroom 1',
        items: [
          { title: 'Clean toilet inside and out' },
          { title: 'Clean shower or tub' },
          { title: 'Clean sink and counter' },
          { title: 'Clean mirror' },
          { title: 'Mop floor' },
          { title: 'Restock toilet paper' },
          { title: 'Restock fresh towels' },
          { title: 'Empty trash' },
          { title: 'Restock toiletries' },
        ],
      },
      {
        name: 'Bathroom 2',
        items: [
          { title: 'Clean toilet inside and out' },
          { title: 'Clean shower or tub' },
          { title: 'Clean sink and counter' },
          { title: 'Clean mirror' },
          { title: 'Mop floor' },
          { title: 'Restock toilet paper' },
          { title: 'Restock fresh towels' },
          { title: 'Empty trash' },
          { title: 'Restock toiletries' },
        ],
      },
      {
        name: 'Kitchen',
        items: [
          { title: 'Wash and put away dishes' },
          { title: 'Clean countertops' },
          { title: 'Clean stovetop' },
          { title: 'Clean microwave inside and out' },
          { title: 'Wipe refrigerator shelves' },
          { title: 'Wipe cabinet fronts' },
          { title: 'Empty trash' },
          { title: 'Mop floor' },
          { title: 'Restock dish soap and sponge' },
        ],
      },
      {
        name: 'Living Room',
        items: [
          { title: 'Dust all surfaces' },
          { title: 'Vacuum or mop floors' },
          { title: 'Fluff pillows and fold blankets' },
          { title: 'Clean TV screen' },
          { title: 'Wipe remotes with disinfectant' },
          { title: 'Empty trash' },
        ],
      },
      {
        name: 'Entryway',
        items: [
          { title: 'Sweep or mop floor' },
          { title: 'Wipe door handle' },
          { title: 'Ensure welcome mat is clean' },
          { title: 'Check lock functions properly' },
        ],
      },
    ],
  },
  {
    id: 'airbnb-3bed-2bath',
    name: 'Airbnb - 3 Bed 2 Bath',
    category: 'Airbnb Recommendations',
    description: 'Perfect for family-sized vacation rentals',
    listTitle: 'Airbnb Turnover Checklist',
    sections: [
      {
        name: 'Bedroom 1',
        items: [
          { title: 'Make bed with fresh linens' },
          { title: 'Dust nightstands and surfaces' },
          { title: 'Empty trash' },
          { title: 'Vacuum or mop floors' },
          { title: 'Check under bed for guest items' },
          { title: 'Wipe light switches and door handles' },
        ],
      },
      {
        name: 'Bedroom 2',
        items: [
          { title: 'Make bed with fresh linens' },
          { title: 'Dust nightstands and surfaces' },
          { title: 'Empty trash' },
          { title: 'Vacuum or mop floors' },
          { title: 'Check under bed for guest items' },
          { title: 'Wipe light switches and door handles' },
        ],
      },
      {
        name: 'Bedroom 3',
        items: [
          { title: 'Make bed with fresh linens' },
          { title: 'Dust nightstands and surfaces' },
          { title: 'Empty trash' },
          { title: 'Vacuum or mop floors' },
          { title: 'Check under bed for guest items' },
          { title: 'Wipe light switches and door handles' },
        ],
      },
      {
        name: 'Bathroom 1',
        items: [
          { title: 'Clean toilet inside and out' },
          { title: 'Clean shower or tub' },
          { title: 'Clean sink and counter' },
          { title: 'Clean mirror' },
          { title: 'Mop floor' },
          { title: 'Restock toilet paper' },
          { title: 'Restock fresh towels' },
          { title: 'Empty trash' },
          { title: 'Restock toiletries' },
        ],
      },
      {
        name: 'Bathroom 2',
        items: [
          { title: 'Clean toilet inside and out' },
          { title: 'Clean shower or tub' },
          { title: 'Clean sink and counter' },
          { title: 'Clean mirror' },
          { title: 'Mop floor' },
          { title: 'Restock toilet paper' },
          { title: 'Restock fresh towels' },
          { title: 'Empty trash' },
          { title: 'Restock toiletries' },
        ],
      },
      {
        name: 'Kitchen',
        items: [
          { title: 'Wash and put away dishes' },
          { title: 'Clean countertops' },
          { title: 'Clean stovetop' },
          { title: 'Clean microwave inside and out' },
          { title: 'Wipe refrigerator shelves' },
          { title: 'Wipe cabinet fronts' },
          { title: 'Empty trash' },
          { title: 'Mop floor' },
          { title: 'Restock dish soap and sponge' },
        ],
      },
      {
        name: 'Living Room',
        items: [
          { title: 'Dust all surfaces' },
          { title: 'Vacuum or mop floors' },
          { title: 'Fluff pillows and fold blankets' },
          { title: 'Clean TV screen' },
          { title: 'Wipe remotes with disinfectant' },
          { title: 'Empty trash' },
        ],
      },
      {
        name: 'Entryway',
        items: [
          { title: 'Sweep or mop floor' },
          { title: 'Wipe door handle' },
          { title: 'Ensure welcome mat is clean' },
          { title: 'Check lock functions properly' },
        ],
      },
    ],
  },

  // ============================================
  // HOMEOWNER TEMPLATES
  // ============================================
  {
    id: 'homeowner-1bed-1bath',
    name: 'Homeowner - 1 Bed 1 Bath',
    category: 'Homeowner Recommendations',
    description: 'Weekly cleaning for small homes',
    listTitle: 'Weekly Cleaning Checklist',
    sections: [
      {
        name: 'Bedroom',
        items: [
          { title: 'Change bed linens' },
          { title: 'Dust furniture and surfaces' },
          { title: 'Vacuum floors and rugs' },
          { title: 'Empty trash' },
          { title: 'Organize closet' },
          { title: 'Wipe mirrors' },
        ],
      },
      {
        name: 'Bathroom',
        items: [
          { title: 'Clean toilet' },
          { title: 'Scrub shower and tub' },
          { title: 'Clean sink and vanity' },
          { title: 'Clean mirror' },
          { title: 'Mop floors' },
          { title: 'Replace towels' },
          { title: 'Empty trash' },
          { title: 'Organize cabinets' },
        ],
      },
      {
        name: 'Kitchen',
        items: [
          { title: 'Clean countertops' },
          { title: 'Clean stovetop and oven' },
          { title: 'Clean microwave' },
          { title: 'Wipe appliances' },
          { title: 'Clean sink' },
          { title: 'Mop floors' },
          { title: 'Empty trash and recycling' },
          { title: 'Wipe cabinet fronts' },
        ],
      },
      {
        name: 'Living Room',
        items: [
          { title: 'Dust furniture and decor' },
          { title: 'Vacuum floors and rugs' },
          { title: 'Wipe electronics' },
          { title: 'Fluff and arrange pillows' },
          { title: 'Empty trash' },
          { title: 'Clean windows' },
        ],
      },
    ],
  },
  {
    id: 'homeowner-2bed-1bath',
    name: 'Homeowner - 2 Bed 1 Bath',
    category: 'Homeowner Recommendations',
    description: 'Weekly cleaning for small to medium homes',
    listTitle: 'Weekly Cleaning Checklist',
    sections: [
      {
        name: 'Primary Bedroom',
        items: [
          { title: 'Change bed linens' },
          { title: 'Dust furniture and surfaces' },
          { title: 'Vacuum floors and rugs' },
          { title: 'Empty trash' },
          { title: 'Organize closet' },
          { title: 'Wipe mirrors' },
        ],
      },
      {
        name: 'Second Bedroom',
        items: [
          { title: 'Change bed linens' },
          { title: 'Dust furniture and surfaces' },
          { title: 'Vacuum floors and rugs' },
          { title: 'Empty trash' },
          { title: 'Organize closet' },
        ],
      },
      {
        name: 'Bathroom',
        items: [
          { title: 'Clean toilet' },
          { title: 'Scrub shower and tub' },
          { title: 'Clean sink and vanity' },
          { title: 'Clean mirror' },
          { title: 'Mop floors' },
          { title: 'Replace towels' },
          { title: 'Empty trash' },
        ],
      },
      {
        name: 'Kitchen',
        items: [
          { title: 'Clean countertops' },
          { title: 'Clean stovetop and oven' },
          { title: 'Clean microwave' },
          { title: 'Wipe appliances' },
          { title: 'Clean sink' },
          { title: 'Mop floors' },
          { title: 'Empty trash and recycling' },
        ],
      },
      {
        name: 'Living Room',
        items: [
          { title: 'Dust furniture and decor' },
          { title: 'Vacuum floors and rugs' },
          { title: 'Wipe electronics' },
          { title: 'Fluff and arrange pillows' },
          { title: 'Empty trash' },
        ],
      },
    ],
  },
  {
    id: 'homeowner-3bed-2bath',
    name: 'Homeowner - 3 Bed 2 Bath',
    category: 'Homeowner Recommendations',
    description: 'Weekly cleaning for family homes',
    listTitle: 'Weekly Cleaning Checklist',
    sections: [
      {
        name: 'Primary Bedroom',
        items: [
          { title: 'Change bed linens' },
          { title: 'Dust furniture and surfaces' },
          { title: 'Vacuum floors and rugs' },
          { title: 'Empty trash' },
          { title: 'Organize closet' },
          { title: 'Wipe mirrors' },
        ],
      },
      {
        name: 'Bedroom 2',
        items: [
          { title: 'Change bed linens' },
          { title: 'Dust furniture and surfaces' },
          { title: 'Vacuum floors and rugs' },
          { title: 'Empty trash' },
          { title: 'Organize closet' },
        ],
      },
      {
        name: 'Bedroom 3',
        items: [
          { title: 'Change bed linens' },
          { title: 'Dust furniture and surfaces' },
          { title: 'Vacuum floors and rugs' },
          { title: 'Empty trash' },
          { title: 'Organize closet' },
        ],
      },
      {
        name: 'Primary Bathroom',
        items: [
          { title: 'Clean toilet' },
          { title: 'Scrub shower and tub' },
          { title: 'Clean sink and vanity' },
          { title: 'Clean mirror' },
          { title: 'Mop floors' },
          { title: 'Replace towels' },
          { title: 'Empty trash' },
        ],
      },
      {
        name: 'Bathroom 2',
        items: [
          { title: 'Clean toilet' },
          { title: 'Scrub shower and tub' },
          { title: 'Clean sink and vanity' },
          { title: 'Clean mirror' },
          { title: 'Mop floors' },
          { title: 'Replace towels' },
          { title: 'Empty trash' },
        ],
      },
      {
        name: 'Kitchen',
        items: [
          { title: 'Clean countertops' },
          { title: 'Clean stovetop and oven' },
          { title: 'Clean microwave' },
          { title: 'Wipe appliances' },
          { title: 'Clean sink' },
          { title: 'Mop floors' },
          { title: 'Empty trash and recycling' },
        ],
      },
      {
        name: 'Living Room',
        items: [
          { title: 'Dust furniture and decor' },
          { title: 'Vacuum floors and rugs' },
          { title: 'Wipe electronics' },
          { title: 'Fluff and arrange pillows' },
          { title: 'Empty trash' },
        ],
      },
      {
        name: 'Dining Room',
        items: [
          { title: 'Dust table and chairs' },
          { title: 'Vacuum or mop floors' },
          { title: 'Clean light fixtures' },
          { title: 'Wipe windows' },
        ],
      },
    ],
  },
];

/**
 * Get all unique categories from templates
 */
export function getTemplateCategories(): string[] {
  const categories = new Set(CHECKLIST_TEMPLATES.map(t => t.category));
  return Array.from(categories);
}

/**
 * Get templates filtered by category
 */
export function getTemplatesByCategory(category: string): ChecklistTemplate[] {
  return CHECKLIST_TEMPLATES.filter(t => t.category === category);
}

/**
 * Search templates by name or description
 */
export function searchTemplates(query: string): ChecklistTemplate[] {
  const lowerQuery = query.toLowerCase();
  return CHECKLIST_TEMPLATES.filter(
    t =>
      t.name.toLowerCase().includes(lowerQuery) ||
      t.description.toLowerCase().includes(lowerQuery) ||
      t.category.toLowerCase().includes(lowerQuery)
  );
}
